import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Motif } from "@/lib/motifs";

/**
 * Snapshot of the home hero as it looked EVEN EARLIER (commit 2827861, 2026-08-13 09:49):
 * The "Editorial Colonnade" hero — before the cinematic dark spotlight.
 *   • Full-bleed lifestyle photo (girl in Butterfly Bliss Pink) with cream editorial vignette on the left
 *   • Kicker: "Chapter One · The Colonnade"
 *   • Headline: "Small clothes, made slowly." (huge serif, ink on cream)
 *   • Featured card: butterfly icon + "On the plinth today" + product, ₹1,199, See the piece + Enter the shop
 *   • Three floating macro hotspot bubbles (The wings / The weave / The button)
 *   • Bottom row: 360° drag hint + "Carved honey sandstone. Dust in golden light."
 */

const HERO_HOTSPOTS = [
  { x: 62, y: 22, label: "The wings", caption: "Eleven hours per butterfly, hand-embroidered." },
  { x: 74, y: 44, label: "The weave", caption: "Organic cotton, hand-dyed in small batches." },
  { x: 68, y: 66, label: "The button", caption: "Coconut wood, never metal." },
];

const HotBubble = ({ h, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="absolute" style={{ left: `${h.x}%`, top: `${h.y}%`, transform: "translate(-50%,-50%)" }} data-testid={`hero-hot-${i}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={h.label}
        className="w-8 h-8 rounded-full hairline hotspot-dot flex items-center justify-center"
        style={{ background: "rgba(246,241,233,0.92)", backdropFilter: "blur(6px)" }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} />
      </button>
      <div className="absolute top-4 left-8 h-[1px] w-24" style={{ background: "var(--stone)" }} />
      {open && (
        <div className="absolute top-1 left-32 w-[220px]" style={{ background: "rgba(246,241,233,0.94)", backdropFilter: "blur(8px)", border: "1px solid var(--stone)" }}>
          <div className="p-3">
            <div className="serif text-base leading-tight">{h.label}</div>
            <div className="text-xs text-ink/70 mt-1 leading-relaxed">{h.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const HomeEditorialPreview = () => {
  return (
    <div data-testid="home-editorial-preview">
      <div
        className="fixed top-3 right-3 z-[60] px-3 py-2 text-[11px] tracking-[0.18em] uppercase"
        style={{ background: "rgba(28,26,25,0.9)", color: "#F6F1E9", border: "1px solid #C9B99F" }}
      >
        Editorial Colonnade · pre 2026-08-14 07:54
      </div>

      {/* HERO — full-bleed lifestyle shot: kid in Butterfly Bliss Pink in colonnade */}
      <section className="relative w-full overflow-hidden" data-testid="hero" style={{ minHeight: "100vh" }}>
        <div className="absolute inset-0">
          <img
            src="/api/uploads/_lifestyle/girls/g-3.webp"
            alt="Butterfly Bliss Pink on a small girl in the Colonnade"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 68%" }}
          />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(90deg, rgba(246,241,233,0.94) 0%, rgba(246,241,233,0.65) 22%, rgba(246,241,233,0.05) 45%, rgba(246,241,233,0) 100%)"
          }} />
          <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: "linear-gradient(180deg, rgba(246,241,233,0) 0%, rgba(246,241,233,0.9) 100%)" }} />
        </div>
        <div className="grain absolute inset-0" />

        <div className="absolute inset-0 hidden lg:block pointer-events-none">
          <div className="relative w-full h-full pointer-events-auto">
            {HERO_HOTSPOTS.map((h, i) => <HotBubble key={i} h={h} i={i} />)}
          </div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 min-h-[100vh] flex flex-col justify-between">
          <div className="max-w-[520px]">
            <div className="nav-item mb-6 flex items-center gap-3 text-ink/70">
              <span className="w-6 h-[1px] bg-ink/50" />
              <span>Chapter One · The Colonnade</span>
            </div>

            <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-ink" data-testid="hero-headline">
              Small clothes,<br />made <em className="italic">slowly</em>.
            </h1>

            <div className="mt-12 max-w-[380px]" data-testid="hero-featured-card">
              <div className="flex items-center gap-3 mb-4">
                <Motif name="butterfly" size={22} strokeColor="var(--accent)" />
                <span className="nav-item text-ink/70">On the plinth today</span>
              </div>
              <div className="serif text-3xl md:text-4xl leading-tight">Butterfly Bliss Pink</div>
              <p className="mt-3 text-ink/80 text-sm leading-relaxed">
                Hand-embroidered on hand-dyed cotton. Coconut wood buttons, side-seam labels,
                back-tie for easy twirls.
              </p>
              <div className="mt-6 flex items-baseline gap-4">
                <span className="serif text-3xl">₹1,199</span>
                <span className="text-xs text-ink/60">· 1–3y</span>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link to="/product/butterfly-bliss-pink" className="btn-solid" data-testid="hero-shop-featured">See the piece</Link>
                <Link to="/shop" className="crimson-link nav-item !text-[12px]" data-testid="hero-shop-all">Enter the shop →</Link>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-end justify-between mt-16 text-xs text-ink/70">
            <span className="flex items-center gap-3">
              <span className="inline-block w-8 h-8 rounded-full hairline flex items-center justify-center">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M4 12 a 8 6 0 0 0 16 0 a 8 6 0 0 0 -16 0" />
                  <path d="M4 12 L 2 10 M4 12 L 2 14" strokeLinecap="round" />
                </svg>
              </span>
              360° · drag to turn
            </span>
            <span className="max-w-[36ch] leading-relaxed text-right">
              Carved honey sandstone. Dust in golden light.
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="nav-item mb-4 text-ink/60">Snapshot</div>
        <h2 className="serif text-3xl md:text-4xl leading-tight mb-6">
          Even earlier — the <em className="italic">Editorial Colonnade</em> hero (13 Aug).
        </h2>
        <ul className="text-ink/80 leading-relaxed space-y-2 text-base list-disc pl-6">
          <li>Cream-vignetted lifestyle photo instead of a dark spotlight — ink text on cream.</li>
          <li>"Chapter One · The Colonnade" kicker and huge serif "Small clothes, made slowly." headline.</li>
          <li>A quiet featured card on the ground with butterfly icon, product, price, and CTA.</li>
          <li>Three floating macro-detail hotspot bubbles (wings / weave / button) — click to open captions.</li>
        </ul>
        <div className="mt-10 flex flex-wrap gap-6">
          <Link to="/" className="crimson-link nav-item !text-[12px]">← Back to current home</Link>
          <Link to="/home-cinematic-preview" className="crimson-link nav-item !text-[12px]">Cinematic version (14 Aug) →</Link>
          <Link to="/home-yesterday-preview" className="crimson-link nav-item !text-[12px]">Flat-mockup version (14 Aug) →</Link>
        </div>
      </div>
    </div>
  );
};

export default HomeEditorialPreview;
